import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, setCurrentUser, removeCurrentUser } from '../utils/auth';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const session = getCurrentUser();
      
      if (session && session.id && session.unique_code) {
        try {
          const userRes = await apiService.getUserById(session.id);
          
          let fullUser = null;
          if (userRes) {
             if (Array.isArray(userRes.data) && userRes.data.length > 0) {
               fullUser = userRes.data[0];
             } else if (userRes.data && typeof userRes.data === 'object') {
               fullUser = userRes.data;
             } else if (!userRes.data && userRes.id) {
                 fullUser = userRes;
             }
          }

          if (fullUser) {
            if (String(fullUser.unique_code) !== String(session.unique_code)) {
              removeCurrentUser();
              setUser(null);
              setLoading(false);
              return;
            }

            if (fullUser.password) delete fullUser.password;

            const userTeamResponse = await apiService.getUserTeamRelations({
              user_id: fullUser.id
            });
            
            const hasTeam = userTeamResponse.data && userTeamResponse.data.length > 0;
            const teamId = hasTeam ? userTeamResponse.data[0].team_id : null;
            
            const userWithTeam = {
              ...fullUser,
              hasTeam,
              teamId
            };
             
            setUser(userWithTeam);
          } else {
            removeCurrentUser();
          }
        } catch (err) {
          console.error("Auth sync error:", err);
          removeCurrentUser();
        }
      } else {
        if (session) removeCurrentUser();
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = (userData) => {
    const safeUser = { ...userData };
    if (safeUser.password) delete safeUser.password;
    setUser(safeUser);
    setCurrentUser(safeUser);
  };

  const logout = () => {
    setUser(null);
    removeCurrentUser();
  };

  const updateUser = (userData) => {
    const safeUser = { ...userData };
    if (safeUser.password) delete safeUser.password;
    setUser(safeUser);
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
    isTeamOwner: user?.is_team_owner === 1
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};